import React, { useState } from "react";
import { Loader2, Plus, X } from "lucide-react";
import closeIcon from "../../assets/cross.svg";

const isValidEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

const emptyRow = () => ({ id: crypto.randomUUID(), firstName: "", lastName: "", email: "" });

/**
 * Manual student invite — one row per student: first name, last name, email.
 *
 * Names are captured (not just emails) because the invite email greets the
 * student by first name; without them it fell back to "Hi <raw address>",
 * which reads like spam. Names are optional so an admin who only has
 * addresses isn't blocked — that case still degrades to the address.
 *
 * Bulk lists belong in Import Roster (AI-extracts names + emails from a
 * spreadsheet/photo), so this modal optimises for a handful of students
 * typed by hand rather than a paste-many-addresses flow.
 */
const InviteStudentModal = ({ onClose, onConfirm }) => {
  const [rows, setRows] = useState([emptyRow()]);
  // True while the invite request is in flight. Disables the buttons (no
  // double-invite) and shows a spinner. The parent closes the modal when
  // onConfirm resolves, so we never reset local state here — resetting
  // mid-request was what flashed an empty form before the modal closed.
  const [isSubmitting, setIsSubmitting] = useState(false);

  const updateRow = (id, field, value) => {
    setRows((prev) => prev.map((r) => (r.id === id ? { ...r, [field]: value } : r)));
  };

  const addRow = () => setRows((prev) => [...prev, emptyRow()]);

  const removeRow = (id) => {
    setRows((prev) => (prev.length === 1 ? [emptyRow()] : prev.filter((r) => r.id !== id)));
  };

  // Only rows with a usable email count; blank spare rows are ignored so a
  // trailing empty row never blocks submission.
  const validRows = rows.filter((r) => isValidEmail(r.email.trim()));
  const hasInvalidEmail = rows.some((r) => r.email.trim() && !isValidEmail(r.email.trim()));

  const handleSendInvite = async () => {
    if (isSubmitting || validRows.length === 0) return;
    try {
      setIsSubmitting(true);
      // Await so the modal stays in its loading state until the invite
      // completes; the parent closes it on success. No local reset — the
      // component unmounts, and resetting here caused the empty-form flash.
      await onConfirm(
        validRows.map((r) => ({
          email: r.email.trim().toLowerCase(),
          firstName: r.firstName.trim() || undefined,
          lastName: r.lastName.trim() || undefined,
        })),
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex justify-center items-center px-4">
      <div className="w-[640px] max-h-[90vh] bg-white rounded-[20px] overflow-hidden font-['Manrope'] flex flex-col">
        {/* Header */}
        <div className="p-6 bg-gradient-to-br from-white to-blue-100 border-b border-blue-100 flex justify-between items-center flex-shrink-0">
          <h2 className="text-[18px] font-bold text-gray-900">Invite Students</h2>
          <button onClick={onClose} className="p-1">
            <img src={closeIcon} alt="Close" className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="p-6 bg-white flex flex-col gap-5 overflow-y-auto">
          <div className="flex flex-col gap-3">
            <div className="grid grid-cols-[1fr_1fr_1.4fr_32px] gap-2 px-1">
              <span className="text-gray-500 text-xs font-semibold uppercase tracking-wide">First name</span>
              <span className="text-gray-500 text-xs font-semibold uppercase tracking-wide">Last name</span>
              <span className="text-gray-500 text-xs font-semibold uppercase tracking-wide">Email address</span>
              <span />
            </div>

            {rows.map((row) => {
              const invalid = row.email.trim() && !isValidEmail(row.email.trim());
              return (
                <div key={row.id} className="grid grid-cols-[1fr_1fr_1.4fr_32px] gap-2 items-center">
                  <input
                    value={row.firstName}
                    onChange={(e) => updateRow(row.id, "firstName", e.target.value)}
                    placeholder="Karen"
                    className="px-3 py-2.5 rounded-lg border border-gray-300 text-sm outline-none focus:border-blue-500"
                  />
                  <input
                    value={row.lastName}
                    onChange={(e) => updateRow(row.id, "lastName", e.target.value)}
                    placeholder="Test"
                    className="px-3 py-2.5 rounded-lg border border-gray-300 text-sm outline-none focus:border-blue-500"
                  />
                  <input
                    type="email"
                    value={row.email}
                    onChange={(e) => updateRow(row.id, "email", e.target.value)}
                    placeholder="karen@school.edu"
                    className={`px-3 py-2.5 rounded-lg border text-sm outline-none focus:border-blue-500 ${
                      invalid ? "border-red-300 bg-red-50" : "border-gray-300"
                    }`}
                  />
                  <button
                    type="button"
                    onClick={() => removeRow(row.id)}
                    aria-label="Remove student"
                    className="w-8 h-8 flex items-center justify-center rounded-full text-gray-400 hover:bg-gray-100 hover:text-gray-600 transition-colors"
                  >
                    <X size={15} />
                  </button>
                </div>
              );
            })}

            <button
              type="button"
              onClick={addRow}
              className="self-start inline-flex items-center gap-1.5 text-sm font-semibold text-blue-600 hover:underline px-1"
            >
              <Plus size={15} />
              Add another student
            </button>
          </div>

          <p className="text-gray-400 text-xs font-normal">
            Names are optional, but including them lets us greet each student by name in the invite email.
            Got a whole class? Use Import Roster instead.
          </p>

          {/* Footer Buttons */}
          <div className="flex justify-end gap-2">
            <button
              onClick={onClose}
              disabled={isSubmitting}
              className="h-11 px-4 py-1.5 text-[16px] font-medium text-gray-900 border border-blue-500 rounded-lg hover:bg-blue-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Cancel
            </button>
            <button
              onClick={handleSendInvite}
              disabled={isSubmitting || validRows.length === 0 || hasInvalidEmail}
              className="h-11 px-4 py-1.5 text-[16px] font-medium text-white rounded-lg transition-colors bg-blue-500 hover:bg-blue-600 disabled:opacity-60 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {isSubmitting && <Loader2 size={16} className="animate-spin" />}
              {isSubmitting
                ? "Sending…"
                : `Send Invite${validRows.length > 1 ? ` (${validRows.length})` : ""}`}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InviteStudentModal;
